import { useState } from 'react';
import {
  BookOpen,
  Briefcase,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  GitCompare,
  Landmark,
  Mail,
  ShieldAlert,
  Clock,
  Users,
  BarChart2,
  AlertTriangle,
  Layers,
  Database,
  Presentation,
  Handshake,
  Scale,
} from 'lucide-react';
import { APP_NAME, APP_PUBLIC_URL, APP_TAGLINE, APP_VERSION, PRODUCT_PITCH } from './productMeta';
import { PAYROLL_GLOSSARY } from './payrollGlossary';
import LegalDisclaimer from './LegalDisclaimer';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'capabilities', label: 'What it checks' },
  { id: 'demo', label: '15-min demo' },
  { id: 'offer', label: 'How we deliver' },
  { id: 'glossary', label: 'Glossary' },
  { id: 'legal', label: 'Legal' },
];

const CAPABILITIES = [
  {
    icon: GitCompare,
    title: 'Payroll recon',
    pitch: 'Oracle element entries vs the ADP payroll register by mapped wage type.',
    details: 'Scopes: payroll WT list, benefits, and G2 time vs register hours. Dollar and hours tolerances.',
  },
  {
    icon: ClipboardCheck,
    title: 'Employment / status',
    pitch: 'Catch status, org, FTE, and salary-basis mismatches before they hit ADP.',
    details: 'Pre-Payroll check. Uses Employee Master when you do not upload a file.',
  },
  {
    icon: Landmark,
    title: 'Banking / direct deposit',
    pitch: 'Flag missing accounts, unsynced bank details, and check-pay methods.',
    details: 'Pre-Payroll check against Oracle payment method and ADP bank infotype.',
  },
  {
    icon: Mail,
    title: 'Email / ESS',
    pitch: 'Confirm work email and ESS communication data match between systems.',
    details: 'Pre-Payroll check. Prevents employees who cannot log in after go-live.',
  },
  {
    icon: FileText,
    title: 'SSL intake',
    pitch: 'Validate wage-type load files staged for ADP before they process.',
    details: 'Upload one or more SSL workbooks; optional Oracle master for person coverage.',
  },
  {
    icon: ShieldAlert,
    title: 'Soft wall / effective dates',
    pitch: 'Find back-dated records ADP will reject after the soft-wall cutoff.',
    details: 'Scan any workbook with an effective-date column.',
  },
  {
    icon: Clock,
    title: 'Time & labor',
    pitch: 'Compare G2 hours sent to ADP with hours actually paid on the register.',
    details: 'Person + wage type. Highlights hours in G2 but not paid, and the reverse.',
  },
  {
    icon: Users,
    title: 'Demographic recon',
    pitch: 'Personal and assignment fields: name, gender, org, and coded-value aliases.',
    details: 'Oracle Employee Master vs ADP master. Normalization so F matches 2.',
  },
  {
    icon: FileText,
    title: 'Live Oracle pulls',
    pitch: 'Run Element Entries and Employee Master from the connected HCM tenant.',
    details: 'Saved files feed recon automatically. Extra ADP catalog reports are runnable for review.',
  },
  {
    icon: BarChart2,
    title: 'Wage type totals',
    pitch: 'Chart ADP register amounts by wage type for a sanity check of the cycle.',
    details: 'Upload the same register used for payroll recon.',
  },
  {
    icon: AlertTriangle,
    title: 'Integration errors',
    pitch: 'Decode ADP transformation / load error workbooks into readable exceptions.',
    details: 'Useful after an Oracle-to-ADP interface run fails.',
  },
  {
    icon: Layers,
    title: 'Mapping Studio',
    pitch: 'Maintain Oracle element ↔ ADP wage type packs and recon exclusions.',
    details: 'OGPI dictionary, WT source list, include/exclude per pair.',
  },
];

const DEMO_STEPS = [
  {
    mins: '0–2',
    title: 'Open Results on a frozen pay period',
    body: 'Do not start on Connections. Show a completed run: exception count, matched pairs, only-Oracle / only-ADP coverage.',
  },
  {
    mins: '2–6',
    title: 'Drill two or three exceptions',
    body: 'Pick an amount mismatch, an unmapped element, and a person only in one system. Export Excel so they see the handoff to payroll.',
  },
  {
    mins: '6–10',
    title: 'Pre-Payroll one check',
    body: 'Banking or email is the fastest “wow.” Show a missing direct-deposit or ESS email that would have hit after go-live.',
  },
  {
    mins: '10–13',
    title: 'Mapping Studio',
    body: 'Show that recon is driven by their wage-type pack, not a black box. Flip Include in recon on one pair.',
  },
  {
    mins: '13–15',
    title: 'Optional live pull',
    body: 'Only if Oracle reports are already running quickly. Otherwise say: “Reports pulls Element Entries and Employee Master from Fusion; we use a saved file in the pitch so we are not waiting ten minutes.”',
  },
];

const OFFER_ROWS = [
  ['You provide (software)', 'Hosted or on-prem recon workbench, mapping packs, Oracle report install, exception export.'],
  ['You provide (services)', 'Kickoff, Oracle report install, wage-type mapping, first-cycle parallel run, training for the payroll analyst.'],
  ['Client provides', 'Oracle HCM tenant + integration user, ADP payroll register, G2 / SSL as needed, named payroll owner.'],
  ['Not in v1', 'Celergo / multi-country packs, single sign-on, payroll-user vs consultant roles, auto-feed of INT026 reports into Pre-Payroll.'],
];

export default function HelpCenter({ onNavigate }) {
  const [tab, setTab] = useState('overview');

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Product</p>
        <h2 className="mt-1 page-heading">{APP_NAME}</h2>
        <p className="content-lead">{APP_TAGLINE}</p>
        <p className="mt-3 max-w-3xl text-sm text-slate-400">{PRODUCT_PITCH}</p>
        <p className="mt-3 text-xs text-slate-500">
          Version {APP_VERSION} ·{' '}
          <a className="text-cyan-400 underline" href={APP_PUBLIC_URL} target="_blank" rel="noreferrer">
            {APP_PUBLIC_URL.replace(/\/$/, '')}
          </a>
          {' '}
          · specialist OGPI validation, not a consumer payroll product
        </p>
      </header>

      <div className="tab-bar flex-wrap">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={tab === id ? 'tab-pill-active' : 'tab-pill-inactive'}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="card-panel p-5 lg:col-span-2">
            <h3 className="text-base font-semibold text-slate-100">The pay-cycle story</h3>
            <ol className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                ['Connect Oracle', 'Save the Fusion tenant. Test once. Report install is a consultant step.'],
                ['Load data', 'Add a payroll period, then pull or upload Element Entries and Employee Master. Client drops the ADP register.'],
                ['Run checks', 'Payroll Recon plus Pre-Payroll (banking, email, SSL, dates, time).'],
                ['Review & export', 'Exceptions by person, element, and amount. Excel / PDF for the payroll team.'],
              ].map(([title, body]) => (
                <li key={title} className="rounded-lg border border-slate-700/60 bg-slate-900/20 p-3">
                  <p className="text-sm font-semibold text-slate-100">{title}</p>
                  <p className="mt-1 text-xs text-slate-400">{body}</p>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" className="btn-primary py-2 text-xs" onClick={() => onNavigate?.('recon')}>
                Open Payroll Recon
              </button>
              <button type="button" className="btn-secondary py-2 text-xs" onClick={() => onNavigate?.('prepayroll')}>
                Open Pre-Payroll
              </button>
            </div>
          </div>
          <div className="card-panel p-5">
            <h3 className="text-base font-semibold text-slate-100">Safe to say in a pitch</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-300">
              <li className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                Live Oracle Element Entries and Employee Master pulls
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                Mapped element ↔ wage type recon with tolerances
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                Pre-close suite: employment, bank, email, SSL, dates, time
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                Exception lists your payroll analyst can export
              </li>
            </ul>
            <p className="mt-4 text-xs text-slate-500">
              Do not claim extra ADP catalog reports (INT026, bank extracts) auto-load into Pre-Payroll. They are
              runnable from Reports for review.
            </p>
          </div>
        </div>
      )}

      {tab === 'capabilities' && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {CAPABILITIES.map(({ icon: Icon, title, pitch, details }) => (
            <article key={title} className="card-panel p-4">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 shrink-0 text-cyan-500" />
                <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
              </div>
              <p className="mt-2 text-sm text-slate-300">{pitch}</p>
              <p className="mt-2 text-xs text-slate-500">{details}</p>
            </article>
          ))}
        </div>
      )}

      {tab === 'demo' && (
        <div className="card-panel p-5">
          <div className="mb-4 flex items-center gap-2">
            <Presentation className="h-5 w-5 text-cyan-500" />
            <h3 className="text-base font-semibold text-slate-100">Fifteen-minute demo script</h3>
          </div>
          <p className="mb-4 text-sm text-slate-400">
            Use one completed pay period with saved Excel files. Do not wait on a live Oracle
            report during the meeting.
          </p>
          <ol className="space-y-3">
            {DEMO_STEPS.map((step) => (
              <li key={step.title} className="flex gap-3 rounded-lg border border-slate-700/60 bg-slate-900/20 p-3">
                <span className="w-12 shrink-0 text-xs font-semibold text-cyan-400">{step.mins}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-100">{step.title}</p>
                  <p className="mt-1 text-xs text-slate-400">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tab === 'offer' && (
        <div className="space-y-4">
          <div className="card-panel p-5">
            <div className="mb-3 flex items-center gap-2">
              <Handshake className="h-5 w-5 text-cyan-500" />
              <h3 className="text-base font-semibold text-slate-100">Managed payroll validation</h3>
            </div>
            <p className="text-sm text-slate-400">
              Sell the outcome — fewer close-cycle surprises — as software plus implementation, not a
              self-serve SaaS download.
            </p>
            <dl className="mt-4 space-y-3">
              {OFFER_ROWS.map(([term, def]) => (
                <div key={term} className="rounded-lg border border-slate-700/60 bg-slate-900/20 px-3 py-2.5">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-cyan-500/90">{term}</dt>
                  <dd className="mt-1 text-sm text-slate-300">{def}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { icon: Database, title: 'Connections', body: 'Named tenants (Dev / Prod). Active connection in the sidebar.' },
              { icon: BookOpen, title: 'User guide', body: 'End-to-end steps are on this Help page for the payroll analyst.' },
              { icon: Briefcase, title: 'Report install', body: 'Install payroll reports from Connections — consultant-led.' },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="card-panel p-4">
                <Icon className="h-4 w-4 text-cyan-500" />
                <p className="mt-2 text-sm font-semibold text-slate-100">{title}</p>
                <p className="mt-1 text-xs text-slate-400">{body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'glossary' && (
        <div className="card-panel overflow-hidden">
          <div className="section-header">
            <h3 className="text-base font-semibold text-slate-100">Payroll glossary</h3>
            <p className="mt-1 text-xs text-slate-500">Same terms as the inline help icons around the app.</p>
          </div>
          <dl>
            {Object.values(PAYROLL_GLOSSARY).map((term) => (
              <div key={term.title} className="grid gap-1 border-b border-slate-700/80 px-5 py-3 last:border-0 sm:grid-cols-4">
                <dt className="text-sm font-semibold text-slate-200">{term.title}</dt>
                <dd className="text-sm text-slate-400 sm:col-span-3">{term.text}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {tab === 'legal' && (
        <div className="card-panel p-5">
          <div className="mb-3 flex items-center gap-2">
            <Scale className="h-5 w-5 text-cyan-500" />
            <h3 className="text-base font-semibold text-slate-100">Trademarks and positioning</h3>
          </div>
          <LegalDisclaimer />
          <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-slate-400">
            <li>Do not describe this as an ADP or Oracle product, module, or certified interface.</li>
            <li>Do not use ADP orange or Oracle red as the primary brand color in sales slides.</li>
            <li>Accounts on a customer-facing host should be created by a consultant — public sign-up is off in production.</li>
          </ul>
        </div>
      )}
    </div>
  );
}
